import type { IComponentController } from "angular";
import { AlarmZoneType } from "App/Modules/Models";

export class AlarmVideoOverlayComponentController implements IComponentController {
    public url: string;

    public w: number;

    public h: number;

    public overlayid: any;

    protected readonly colorAlarmZone: string = "#FF0000";

    protected readonly colorBoundingBox: string = "#FFFF00";

    protected readonly colorShadow: string = "#000000";

    protected readonly strokeWidth: string = "1";

    protected readonly triggeredStrokeWidth: string = "2";

    protected readonly dashSpacing: string = "4 2";

    protected readonly shadowOffset: number = 1;

    protected readonly svgNamespace: string = "http://www.w3.org/2000/svg";

    public $onInit() {
        const request = new Request(this.url);

        window
            .fetch(request)
            .then((response) => response.json())
            .then((response) => this.render(response))
            .catch(() => {
                console.log("Couldn't fetch alarm zone data");
            });
    }

    private render(data: any) {
        const svgRoot = document.getElementById(`${this.overlayid}`);
        if (svgRoot === null) return;

        if (data.triggeredAlarmZone) {
            const alarmType = data.triggeredAlarmZone.type;
            if (
                alarmType === AlarmZoneType[AlarmZoneType.MotionDetection] ||
                alarmType === AlarmZoneType[AlarmZoneType.LoiteringDetection] ||
                alarmType === AlarmZoneType[AlarmZoneType.ViolentBehaviorDetection] ||
                alarmType === AlarmZoneType[AlarmZoneType.FallDetection] ||
                alarmType === AlarmZoneType[AlarmZoneType.UnattendedObjectDetection]
            ) {
                const alarmZones = data.alarmZones.map((az) => this.getAlarmZoneElement(az));
                const triggeredAlarmZone = this.getAlarmZoneElement(data.triggeredAlarmZone, true);

                alarmZones.forEach((az) => {
                    svgRoot.appendChild(az);
                });
                svgRoot.appendChild(triggeredAlarmZone);
            }

            if (
                alarmType === AlarmZoneType[AlarmZoneType.MotionDetection] ||
                alarmType === AlarmZoneType[AlarmZoneType.ViolentBehaviorDetection] ||
                alarmType === AlarmZoneType[AlarmZoneType.FallDetection] ||
                alarmType === AlarmZoneType[AlarmZoneType.UnattendedObjectDetection]
            ) {
                const boundingBoxes = data.boundingBoxes.map((bb) => this.getBoundingBoxElement(bb));
                boundingBoxes.forEach((bb) => {
                    svgRoot.appendChild(bb);
                });

                const blobPositionHistory = this.getBlobPositionHistoryElement(data);
                svgRoot.appendChild(blobPositionHistory);
            }
        } else {
            // Flame detection
            const boundingBoxes = data.boundingBoxes?.map((bb) => this.getBoundingBoxElement(bb));
            if (boundingBoxes) {
                boundingBoxes.forEach((bb) => {
                    svgRoot.appendChild(bb);
                });
            }
        }
    }

    private getAlarmZoneElement(data: any, triggered = false) {
        const g = document.createElementNS(this.svgNamespace, "g");
        if (triggered) {
            g.setAttribute("key", `zone-triggered-${data.id}`);
            g.setAttribute("class", "alarm-video-overlay triggered-alarm-zone-overlay");
        } else {
            g.setAttribute("key", `zone-${data.id}`);
            g.setAttribute("fill", "none");
            g.setAttribute("stroke", this.colorAlarmZone);
            g.setAttribute("stroke-width", this.strokeWidth);
        }

        const points = data.polygon.map((c, index) => {
            const prefix = index === 0 ? "M" : "L";
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            return `${prefix + c.x * (this.w - 1)} ${c.y * (this.h - 1)}`; // normalized, convert to element space
        });

        const path = document.createElementNS(this.svgNamespace, "path");
        path.setAttribute("d", `${points.join(" ")} Z`);
        path.setAttribute("vector-effect", "non-scaling-stroke");

        g.appendChild(path);

        return g;
    }

    private getBoundingBoxElement(blobRectangle: any) {
        const g = document.createElementNS(this.svgNamespace, "g");
        const gBox = document.createElementNS(this.svgNamespace, "g");
        const gShadow = document.createElementNS(this.svgNamespace, "g");

        gBox.setAttribute("fill", "none");
        gBox.setAttribute("stroke", this.colorBoundingBox);
        gBox.setAttribute("stroke-width", this.strokeWidth);
        gBox.setAttribute("stroke-dasharray", this.dashSpacing);
        const rectBox = document.createElementNS(this.svgNamespace, "rect");
        rectBox.setAttribute("x", blobRectangle.x);
        rectBox.setAttribute("y", blobRectangle.y);
        rectBox.setAttribute("width", blobRectangle.width);
        rectBox.setAttribute("height", blobRectangle.height);
        rectBox.setAttribute("vector-effect", "non-scaling-stroke");
        gBox.appendChild(rectBox);
        // gBox.setAttribute("key", `bbox-contour-${data.id}`); // Will cause duplicates for multiple bounding boxes

        gShadow.setAttribute("fill", "none");
        gShadow.setAttribute("stroke", this.colorShadow);
        gShadow.setAttribute("stroke-width", this.strokeWidth);
        gShadow.setAttribute("stroke-dasharray", this.dashSpacing);
        const rectShadow = document.createElementNS(this.svgNamespace, "rect");
        rectShadow.setAttribute("x", (parseInt(blobRectangle.x, 10) + this.shadowOffset).toString());
        rectShadow.setAttribute("y", (parseInt(blobRectangle.y, 10) + this.shadowOffset).toString());
        rectShadow.setAttribute("width", blobRectangle.width);
        rectShadow.setAttribute("height", blobRectangle.height);
        rectShadow.setAttribute("vector-effect", "non-scaling-stroke");
        gShadow.appendChild(rectShadow);
        // gBox.setAttribute("key", `bbox-shadow-${data.id}`);

        g.appendChild(gShadow);
        g.appendChild(gBox);
        // g.setAttribute("key", `bbox-${data.id}`);
        return g;
    }

    private getBlobPositionHistoryElement(data: any) {
        const g = document.createElementNS(this.svgNamespace, "g");
        g.setAttribute("fill", "none");
        g.setAttribute("stroke", this.colorBoundingBox);
        g.setAttribute("stroke-width", this.strokeWidth);
        g.setAttribute("stroke-dasharray", this.dashSpacing);

        const points = data.blobPositionHistory.map((c, index) => {
            const prefix = index === 0 ? "M" : "L";
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            return `${prefix + c.x} ${c.y}`;
        });

        const path = document.createElementNS(this.svgNamespace, "path");
        path.setAttribute("d", points.join(" "));
        path.setAttribute("vector-effect", "non-scaling-stroke");

        g.appendChild(path);
        g.setAttribute("key", `history-${data.id}`);

        return g;
    }
}
